import React, { useMemo, useRef, useEffect, useState } from 'react';
import _ from 'lodash';
import { Grid, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { SchedulingRecordTypes } from '../../types/types';
import { LocalAtRecord } from '../../db';
import DayShift from './DayShift';
import { GroupedVirtuoso } from 'react-virtuoso';

type SchedulingGroupedProps = {
  scheduledGrouped: Record<string, Record<string, SchedulingRecordTypes[]>>;
  hiddenScheduled: string[];
  updateHiddenScheduled: (hidden: string[]) => void;
  selectedShift: string;
  updateSelectedShift: (shift: string) => void;
  updateZoomField: (zoomField: LocalAtRecord) => void;
  onRolloverShiftClick: () => void;
  onRolloverJobClick: (job: LocalAtRecord<JobsFieldSet>) => void;
};

const SchedulingGrouped: React.FC<SchedulingGroupedProps> = ({
  scheduledGrouped,
  hiddenScheduled,
  updateHiddenScheduled,
  selectedShift,
  updateSelectedShift,
  updateZoomField,
  onRolloverShiftClick,
  onRolloverJobClick,
}) => {
  const virtuosoRef = useRef<any>(null);
  const [scrollToItemIndex, setScrollToItemIndex] = useState<number | null>(null);
  // State to track expanded groups
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const groupKeys = useMemo(() => Object.keys(scheduledGrouped).sort(), [scheduledGrouped]);

  // Build grouping counts & items flattened for virtuoso
  const data = useMemo(() => {
    // For each group, decide how many children (dayShifts) it will show if expanded.
    // E.g. groupCounts: number of children per group (0 if collapsed)
    const groupCounts: number[] = groupKeys.map((g) => {
      return expandedGroups[g] ? Object.keys(scheduledGrouped[g]).length : 0;
    });

    // Flatten all items, in order, grouped by group.
    // Virtuoso expects data as a flat array of *all items in all groups*, in group order
    const flatItems = groupKeys.flatMap((g) => {
      if (expandedGroups[g]) {
        return Object.keys(scheduledGrouped[g]).map((dayShiftKey) => ({
          groupKey: g,
          dayShiftKey,
        }));
      } else {
        return []; // no items if collapsed
      }
    });

    return { groupCounts, flatItems };
  }, [scheduledGrouped, expandedGroups]);

  // Restore scroll position after accordion expansion
  useEffect(() => {
    if (scrollToItemIndex !== null && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: scrollToItemIndex,
        align: 'start', // Keep the accordion header at the top
        behavior: 'auto', // Immediate scroll without animation
      });
    }
  }, [scrollToItemIndex]);

  return (
    <div style={{ display: 'block', height: '100vh' }}>
      <GroupedVirtuoso
        ref={virtuosoRef}
        style={{ height: '100%', width: '100%' }}
        groupCounts={data.groupCounts}
        components={{
          // To override sticky header behavior, we need to override Group and TopItemList to reset the position style
          Group: (props) => <div {...props} style={{ position: 'relative', top: 'auto', zIndex: 1 }} />,
          TopItemList: (props) => <div {...props} style={{ position: 'relative', top: 'auto', zIndex: 1 }} />,
        }}
        computeItemKey={(index) => data.flatItems[index]?.dayShiftKey || index}
        // followOutput={false}
        groupContent={(index) => {
          const groupKey = groupKeys[index];
          const isExpanded = !!expandedGroups[groupKey];

          return (
            <div
              style={{
                width: '100%',
                padding: '10px 5px',
                cursor: 'pointer',
              }}
              onClick={() => {
                setExpandedGroups((prev) => ({
                  ...prev,
                  [groupKey]: !prev[groupKey],
                }));
              }}
            >
              <Grid container justifyContent="space-between" alignItems="center" style={{ zIndex: 0 }} size={12}>
                <Typography>{groupKey}</Typography>

                <Typography>{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Typography>
              </Grid>
            </div>
          );
        }}
        itemContent={(itemIndex) => {
          const dataShift = data.flatItems[itemIndex];

          // item is from flatItems
          if (!dataShift) {
            return null;
          }

          return (
            <DayShift
              dayShiftKey={dataShift.dayShiftKey}
              scheduled={scheduledGrouped[dataShift.groupKey]}
              hiddenScheduled={hiddenScheduled}
              updateHiddenScheduled={updateHiddenScheduled}
              selectedShift={selectedShift}
              updateSelectedShift={updateSelectedShift}
              updateZoomField={updateZoomField}
              onRolloverShiftClick={onRolloverShiftClick}
              onRolloverJobClick={onRolloverJobClick}
              onAccordionToggle={() => setScrollToItemIndex(itemIndex)} // Notify on accordion toggle
            />
          );
        }}
      />
    </div>
  );
};

export default SchedulingGrouped;
